import axios from 'axios';
import { urls, TIMERS } from '../utils/constants';
import { parseJwt } from '../utils/functions';

class Api {
    constructor() {
        this._baseURL = urls.BASE_API;
        this._api = axios.create({
            baseURL: this._baseURL,
            timeout: TIMERS.TIMEOUT,
        });
        this._token = null;
        this._session = null;
    }

    // eslint-disable-next-line class-methods-use-this
    isLogged() {
        const token = JSON.parse(localStorage.getItem('KopaLive'));
        if (token) {
            if (parseJwt(token?.accessToken)?.force_pw_change === true) {
                return false;
            }
            return true;
        }
        return false;
    }

    getAccessToken() {
        return this._token;
    }

    getSessionToken() {
        return this._session.session_token;
    }

    async login(username, password) {
        const { data: token } = await this._api.post('auth/login', { username, password });
        this._token = token.accessToken;
        this._api.defaults.headers.common.Authorization = `Bearer ${this._token}`;
        return { token, appMerch: token.appMerch };
    }

    async recoverPassword({ email }) {
        await this._api.post('auth/recover', { username: email, useCode: true });
    }

    async verifyRecoverCode(email, code) {
        const { data } = await this._api.post('auth/verify_recover_code', { username: email, recover_code: code });
        return data;
    }

    async changePassword(password, token) {
        await this._api.post('auth/change', { password }, { headers: { Authorization: `Bearer ${token}` } });
    }

    async logout() {
        try {
            if (this._token) {
                await this._api.post('auth/logout');
            }
        } finally {
            this.clear();
        }
    }

    editProfile(profile) {
        const { social_medias, thumbnail_url } = profile;
        return this._api.put('/users/profile', { social_medias, thumbnail_url }).then(res => res);
    }

    setToken(token) {
        this._token = token.accessToken;
        this._api.defaults.headers.common.Authorization = `Bearer ${token.accessToken}`;
    }

    clear() {
        this._token = null;
        this._api.defaults.headers.common.Authorization = null;
    }

    getRooms() {
        return this._api.get('rooms/').then(r => r.data);
    }

    getRoom(roomID, producerToken) {
        const sessionToken = this.getSessionToken();
        return this._api.get(`rooms/${roomID}`, { params: { session_token: sessionToken, producer_token: producerToken } }).then(r => r.data);
    }

    async getRoomPriorityVideoUrl(roomId, producerToken) {
        const sessionToken = this.getSessionToken();
        return this._api.post(`/rooms/${roomId}/video_priority`, undefined, { params: { session_token: sessionToken, producer_token: producerToken } }).then(r => r.data);
    }

    getUserRooms() {
        return this._api.get('rooms/list/all').then(r => r.data);
    }

    getRoomEdit(roomID) {
        return this._api.get(`rooms/info/${roomID}`).then(r => r.data);
    }

    createRoom(room) {
        return this._api.post('rooms/', room).then(r => r.data);
    }

    editRoom(room, roomID) {
        return this._api.put(`rooms/${roomID}`, room).then(r => r.data);
    }

    activeGoLive(roomID) {
        return this._api.post(`rooms/go/live/${roomID}`).then(r => r.data);
    }

    activeEndLive(roomID) {
        return this._api.post(`rooms/end/live/${roomID}`).then(r => r.data);
    }

    isRoomLive(roomID) {
        return this._api.get(`rooms/live/status/${roomID}`).then(r => r.data);
    }

    getRoomEngagement(roomID) {
        return this._api.get(`rooms/engagement/report/${roomID}`).then(r => r.data);
    }

    getRoomEventLog(roomID) {
        return this._api.get(`rooms/eventlog/report/${roomID}`).then(r => r.data);
    }

    getRoomGraph(roomID, startDate, endDate) {
        return this._api.get(`rooms/graph/report/${roomID}${startDate ? `?fromDate=${startDate}` : ''}${endDate ? `&toDate=${endDate}` : ''}`).then(r => r.data);
    }

    getRoomGeolocalization(roomID, startDate, endDate) {
        return this._api.get(`rooms/geolocalization/report/${roomID}${startDate ? `?fromDate=${startDate}` : ''}${endDate ? `&toDate=${endDate}` : ''}`).then(r => r.data);
    }

    sendMessage(roomID, text) {
        return this._api.post(`/rooms/${roomID}/message`, { text }).then(r => r.data);
    }

    sendQuestion(roomID, text) {
        return this._api.post(`/rooms/${roomID}/message/question`, { text }).then(r => r.data);
    }

    sendReply(roomID, text, originalMessageId) {
        return this._api.post(`/rooms/${roomID}/message/reply`, { text, originalMessageId }).then(r => r.data);
    }

    sendMessageMonitorChat(roomID, text) {
        return this._api.post(`/rooms/${roomID}/message/instructor`, { text }).then(r => r.data);
    }

    sendMessageTeamChat(roomID, text) {
        return this._api.post(`/rooms/${roomID}/message/team`, { text }).then(r => r.data);
    }

    pinMessage(roomID, messageID) {
        return this._api.post(`/rooms/pin/message/${messageID}/${roomID}`).then(r => r.data);
    }

    unpinMessage(roomID, messageID) {
        return this._api.post(`/rooms/unpin/message/${messageID}/${roomID}`).then(r => r.data);
    }

    enterChat(roomID) {
        const sessionToken = this.getSessionToken();
        return this._api.post(`/rooms/${roomID}/enter`, { session_token: sessionToken }).then(r => r.data);
    }

    exitChat(roomID) {
        const sessionToken = this.getSessionToken();
        return this._api.post(`/rooms/${roomID}/exit`, { session_token: sessionToken }).then(r => r.data);
    }

    getVideo(videoID) {
        return this._api.get(`video/${videoID}`).then(r => r.data);
    }

    setChatEnabled(roomId, enabled) {
        return this._api.post(`config/${roomId}/chat/enable`, { enabled });
    }

    getVideoUploadUrl(payload) {
        return this._api.post('rooms/upload/component', payload).then(r => r.data);
    }

    // teams
    getTeamsEnable(roomID) {
        return this._api.get(`config/${roomID}/teams/enable`).then(r => r.data);
    }

    getTeams(roomID) {
        return this._api.get(`config/${roomID}/teams`).then(r => r.data);
    }

    setTeamsStatus(status, roomID) {
        return this._api.post(`config/${roomID}/teams/enable`, { enabled: status }).then(r => r.data);
    }

    createTeams(roomID, size) {
        return this._api.post(`config/${roomID}/teams/create`, { size }).then(r => r.data);
    }

    // offers
    getOffers(roomID) {
        return this._api.get(`rooms/${roomID}/sale`).then(r => r.data);
    }

    getOfferUrl(roomID) {
        return this._api.get(`rooms/${roomID}/sale/url`).then(r => r.data);
    }

    setOffersStatus(status, roomID) {
        return this._api.put(`rooms/${roomID}/sale/enable`, { enabled: status }).then(r => r.data);
    }

    createOffer(roomID, templateUrl, useSalesforce) {
        return this._api.put(`rooms/${roomID}/sale`, { saleBaseUrl: templateUrl, useSalesForceId: useSalesforce }).then(r => r.data);
    }

    // vouchers
    verifyVoucher(voucherId) {
        return this._api.get(`vouchers/${voucherId}/check/status`).then(r => r.data);
    }

    registerVoucherUser(voucher) {
        return this._api.post('vouchers/users/register', voucher).then(r => r.data);
    }

    verifyEmail(voucherId, code) {
        return this._api.post(`vouchers/users/verify?voucherId=${voucherId}&code=${code}`).then(r => r.data);
    }

    resendEmail(email) {
        return this._api.post(`vouchers/users/resend/email?username=${email}`).then(r => r.data);
    }

    verifyPassword(voucherId, password) {
        return this._api.post('vouchers/users/register/password', { voucherId, password }).then(r => r.data);
    }

    associateVoucher(voucherId) {
        return this._api.put(`vouchers/${voucherId}/associate`).then(r => r.data);
    }

    associateVoucherRegistered(voucherId, renew) {
        return this._api.put(`vouchers/${voucherId}/users/associate?renew=${renew}`).then(r => r.data);
    }

    getUser() {
        return this._api.get('auth/me');
    }

    changeUserLanguage(language) {
        return this._api.post(`config/change/language/${language}`);
    }

    setUserTerm(courseId, userId) {
        return this._api.put(`users/${userId}/accept/course/terms/${courseId}`);
    }

    getQuizInfo(quizId) {
        return this._api.get('/rooms/questions/info', { params: { id: quizId, groups: true } }).then(r => r.data);
    }

    answerQuiz(questionId, answer) {
        return this._api.post(`/rooms/questions/${questionId}/answer?answer=${answer}`).then(r => r.data);
    }

    answerQuizWithGroups(quizId, answers = []) {
        return this._api.post(`/rooms/questions/${quizId}/answer`, { answers }).then(r => r.data);
    }

    blockUser(roomId, userId) {
        return this._api.post(`/rooms/${roomId}/chat/block`, { userId, block: true }).then(r => r.data);
    }

    getPersonalRanking(courseId) {
        return this._api.get(`${urls.REACT_GAMES_API}ranking/personal`, { params: { courseId } }).then(r => r.data);
    }

    getGamificationCourseStatus(courseId) {
        return this._api.get(`${urls.REACT_GAMES_API}course/${courseId}/status`).then(r => r.data);
    }

    async createSession() {
        const resultSessionToken = await this._api.post('/auth/session').then(r => r.data);
        this._session = resultSessionToken;
        return resultSessionToken;
    }

    getUserOnBoarding() {
        return this._api.get('/users/onboarding').then(r => r.data);
    }

    setOnBoarding(key, skipped, global_skip) {
        let data = {};
        if (global_skip !== undefined) {
            data = { global_skip };
        } else if (skipped !== undefined) {
            data = { key, skipped };
        } else {
            data = { key };
        }
        return this._api.put('/users/onboarding', data).then(r => r.data);
    }

    getWebRTCServerTime() {
        return this._api.get(`${urls.WEBRTC_LIVESTREAM}time/`).then(r => r.data);
    }

    sendWebRTCStatistics(roomId, stats) {
        return this._api.post(`${urls.WEBRTC_LIVESTREAM}statistics/${roomId}`, stats).then(r => r.data);
    }

    checkProducerPermission(roomId, producerToken) {
        return this._api.post(`${urls.WEBRTC_LIVESTREAM}token/check-type`, { roomId, token: producerToken });
    }

    async appMerchRequest(showAppMerchAgain) {
        await this._api.put('/users/appmerch', { appMerch: showAppMerchAgain });
    }

    getWatchedVideosProgress(idsList = []) {
        return this._api.post('/rooms/progress', { ids_list: idsList }).then(r => r.data);
    }
}

const api = new Api();

export default api;
